import { useEffect, useState } from "react";
import {
  useLocationsAssignedToUserLazyQuery,
  useGetSurveyResponsesLazyQuery,
} from "pacts/app-webcore/hasura-webcore.graphql";
import { useAuth0 } from "services/auth/authService";
import logger from "services/logger/logger";
import useRoleAndPermission from "./use-role-and-permission";

/**
 * Hook to check if a user has at least one location and is already onboarded in SmartBEA
 * Returns true if the user should be restricted from accessing SmartBEA (has location and is onboarded)
 */
const useUserSmartBEAStatus = () => {
  const [hasLocation, setHasLocation] = useState<boolean>(false);
  const [isOnboarded, setIsOnboarded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const { user, isAuthenticated } = useAuth0();
  const { isPC } = useRoleAndPermission();

  // Query to check if user has locations assigned
  const [fetchLocationsAssignedToUser, { loading: locationsLoading }] = useLocationsAssignedToUserLazyQuery({
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const locationCount = data.locationsAssignedToUser.locations.length;
      const hasLocations = locationCount > 0;
      setHasLocation(hasLocations);

      // If user has more than one location, automatically restrict access to SmartBEA
      if (locationCount > 1) {
        setIsOnboarded(true); // Consider them onboarded to trigger restriction
        setLoading(false);
        return;
      }

      // If user has exactly one location, check if it has a survey response
      if (hasLocations) {
        // Get the location ID to check for survey responses
        const locationId = data.locationsAssignedToUser.locations[0].id;
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        checkSurveyResponses(locationId);
      } else {
        // No locations, so not onboarded
        setIsOnboarded(false);
        setLoading(false);
      }
    },
    onError: (error) => {
      logger.error("Error checking user locations:", error);
      setHasLocation(false);
      setIsOnboarded(false);
      setLoading(false);
    },
  });

  // Query to check if a location has survey responses (meaning it's onboarded)
  const [getSurveyResponses, { loading: surveyResponsesLoading }] = useGetSurveyResponsesLazyQuery({
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const hasResponses = data?.sensorflow_survey_responses && data.sensorflow_survey_responses.length > 0;
      setIsOnboarded(hasResponses);
      setLoading(false);
    },
    onError: (error) => {
      logger.error("Error checking survey responses:", error);
      setIsOnboarded(false);
      setLoading(false);
    },
  });

  const checkSurveyResponses = (locationId: string) => {
    getSurveyResponses({
      variables: {
        locationId,
        limit: 1, // We only need to know if there's at least one response
      },
    });
  };

  useEffect(() => {
    // Only check if user is authenticated
    if (isAuthenticated && user) {
      fetchLocationsAssignedToUser();
    } else {
      // Not authenticated, so not restricted
      setHasLocation(false);
      setIsOnboarded(false);
      setLoading(false);
    }
  }, [isAuthenticated, user, fetchLocationsAssignedToUser]);

  return {
    // Project Coordinator roles should always have access to SmartBEA page
    isRestricted: isPC() ? false : hasLocation && isOnboarded,
    hasLocation,
    isOnboarded,
    loading: loading || locationsLoading || surveyResponsesLoading,
  };
};

export default useUserSmartBEAStatus;
